import React, { useState, useEffect } from 'react';
import { useAuth } from 'modules/auth/context/AuthContextV2';
import { notificationService } from 'services/notification-v2.service';
import Button from 'components/v2/button/Button';

interface NotificationPromptProps {
  // Time to wait before showing the prompt (in milliseconds)
  delayMs?: number;
  // Optional callback when permission is granted
  onPermissionGranted?: () => void;
}

/**
 * NotificationPrompt - A user-friendly UI component for requesting notification permissions
 *
 * Shows a banner explaining the benefits of notifications and requests permission
 * only after the user has context and explicitly chooses to enable them.
 */
const NotificationPrompt: React.FC<NotificationPromptProps> = ({
  delayMs = 2000, // Default delay of 2 seconds
  onPermissionGranted,
}) => {
  const { user } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  useEffect(() => {
    if (!user?._id) return;

    let timer: ReturnType<typeof setTimeout>;

    const checkPermissionStatus = async () => {
      try {
        // Get current permission state
        const permission = await notificationService.getPermissionStatus();

        // Only show for users in 'default' state (haven't made a decision yet)
        if (permission === 'default') {
          // Delay showing the prompt for better UX
          timer = setTimeout(() => {
            setShowPrompt(true);
          }, delayMs);
        }
      } catch (error) {
        console.error('Error checking notification permission:', error);
      }
    };

    checkPermissionStatus();

    // Clean up timer on unmount
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [user, delayMs]);

  const handleEnableNotifications = async () => {
    if (!user?._id) return;

    setIsRequesting(true);
    try {
      const success = await notificationService.requestPermission();

      if (success) {
        setShowPrompt(false);
        if (onPermissionGranted) {
          onPermissionGranted();
        }
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#2A2B4A] font-manrope shadow-lg z-50">
      <div>
        <div className="flex flex-col items-center justify-between gap-2">
          <div className="flex-1">
            <h3 className="font-medium text-[1.25rem]">
              Stay updated with notifications
            </h3>
            <p className="font-medium text-[0.875rem] opacity-80">
              Get timely alerts about important updates and activity in your
              account
            </p>
          </div>
          <div className="flex gap-3 ml-4 self-end">
            <Button
              variant="destructive"
              onClick={handleDismiss}
              className="px-5 py-3 text-white"
              disabled={isRequesting}
            >
              Later
            </Button>
            <Button
              onClick={handleEnableNotifications}
              className="py-3 px-5 whitespace-nowrap"
              disabled={isRequesting}
            >
              {isRequesting ? 'Enabling...' : 'Enable Notifications'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
